import React, { useState, KeyboardEvent } from 'react';
import AudioPlayer from 'components/AudioPlayer';
import { EpisodeAudios } from 'modules/AnchorAPI/v3/episodes/fetchEpisodeOverview';
import { Button } from 'shared/Button/NewButton';
import PlayButton from 'components/PlayButton';
import { getFormattedTimestamp } from 'modules/Time';
import { formatBytes } from 'modules/NumberFormatter';
import {
  calculateAggregateDuration,
  calculateTotalDuration,
} from 'screens/EpisodeOptionsScreen/components/EpisodeContent/utils';
import {
  MediaPreviewContentWrapper,
  MediaPreviewPlayerWrapper,
  VideoDisclaimerWrapper,
  uploaderEditEpisodeButtonStyle,
} from './styles';
import {
  MediaInfoContainer,
  uploaderCancelButtonStyle,
  UploaderContainer,
  UploaderLabelsContainer,
  UploaderStatusContainer,
  UploaderStatusHeading,
  UploaderStatusLabel,
} from '../styles';
import { MultiFileProgressBar } from './MultiFileProgressBar';

export type MediaPreviewStateProps = {
  episodeAudios: EpisodeAudios[];
  isVideo: boolean;
  title: string;
  webEpisodeId: string;
  totalSize: number;
  onClickDelete: () => void;
  onClickReplace: () => void;
  isEpisodePublished: boolean;
};

export const MediaPreviewState = ({
  episodeAudios,
  isVideo,
  title,
  webEpisodeId,
  onClickDelete,
  onClickReplace,
  totalSize,
  isEpisodePublished,
}: MediaPreviewStateProps) => {
  const playableEpisodeAudios = episodeAudios.filter(
    audio => audio.type !== 'ad'
  );
  const [doSeek, setDoSeek] = useState(false);
  // Playback positions are in seconds
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [localPlaybackPosition, setLocalPlaybackPosition] = useState(0);
  const [activeAudioIndex, setActiveAudioIndex] = useState(-1);

  const playableAudioDuration = calculateTotalDuration(playableEpisodeAudios);
  const aggregateDuration = calculateAggregateDuration(playableEpisodeAudios);

  /**
   * Returns the timestamp (in s) of the current position within the
   * episodeAudio segment denoted by audioIndex.
   */
  const getLocalPlaybackPosition = (
    audioIndex: number,
    globalPlaybackPosition: number
  ) => {
    return (
      globalPlaybackPosition -
      (audioIndex === 0 ? 0 : aggregateDuration[audioIndex - 1])
    );
  };

  /**
   * Returns the timestamp (in s) of the current position within the entire
   * episode (all the segments in episodeAudios).
   */
  const getGlobalPlaybackPosition = (
    audioIndex: number,
    activeAudioPlaybackPosition: number
  ) => {
    const previousAudiosLength =
      audioIndex === 0 ? 0 : aggregateDuration[audioIndex - 1];

    return previousAudiosLength + activeAudioPlaybackPosition;
  };

  const onPlayOrPause = () => {
    if (activeAudioIndex === -1) {
      setActiveAudioIndex(0);
    }
    setIsPlaying(!isPlaying);
  };

  const onProgressBarClick = (results: {
    localAudioPosition: number;
    activeIndex: number;
    playbackAudioPosition: number;
  }) => {
    const { activeIndex, localAudioPosition, playbackAudioPosition } = results;

    setDoSeek(true);
    setPlaybackPosition(playbackAudioPosition);
    setActiveAudioIndex(activeIndex);
    setLocalPlaybackPosition(localAudioPosition);

    if (!isPlaying) {
      setIsPlaying(true);
    }
  };

  const onProgressBarKeyDown = (e: KeyboardEvent) => {
    switch (e.keyCode) {
      case 32:
      case 13:
        onPlayOrPause();
    }
  };

  const handlePlayNextSegment = () => {
    const lastAudioIndex = playableEpisodeAudios.length - 1;
    if (activeAudioIndex === lastAudioIndex) {
      setIsPlaying(false);
      setPlaybackPosition(0);
      setLocalPlaybackPosition(0);
      setActiveAudioIndex(-1);
    } else {
      const nextIndex = activeAudioIndex + 1;
      setPlaybackPosition(getGlobalPlaybackPosition(nextIndex, 0));
      setActiveAudioIndex(nextIndex);
      setLocalPlaybackPosition(0);
      setIsPlaying(true);
    }
  };
  const byteString = totalSize ? `${formatBytes(totalSize)} | ` : '';
  return (
    <MediaPreviewContentWrapper>
      <MediaPreviewPlayerWrapper>
        {playableEpisodeAudios.map((audio: EpisodeAudios, i: number) => {
          return (
            <AudioPlayer
              actions={{
                setDoSeek: (isSeeking: boolean) => {
                  setDoSeek(isSeeking);
                },
                updatePlaybackPosition: (playbackPositionInSeconds: number) => {
                  const globalPosition = getGlobalPlaybackPosition(
                    activeAudioIndex,
                    playbackPositionInSeconds
                  );
                  setPlaybackPosition(globalPosition);
                  setLocalPlaybackPosition(
                    getLocalPlaybackPosition(activeAudioIndex, globalPosition)
                  );
                },
              }}
              playbackPosition={localPlaybackPosition}
              doSeek={doSeek}
              doPreload={true}
              isActive={i === activeAudioIndex}
              isPlaying={isPlaying}
              onEnded={() => {
                handlePlayNextSegment();
              }}
              onPlayOrPause={onPlayOrPause}
              key={`audio-player-${audio.webAudioId}-${aggregateDuration[i]}`}
              url={audio.url}
            />
          );
        })}
        <UploaderContainer>
          <MediaInfoContainer>
            <PlayButton
              isPlaying={isPlaying}
              size={46}
              colors={{ colorClass: 'black' }}
              onClick={onPlayOrPause}
              ariaLabel="Play episode audio"
            />
            <UploaderLabelsContainer>
              <UploaderStatusHeading>{title}</UploaderStatusHeading>
              <UploaderStatusLabel>
                {byteString}
                {getFormattedTimestamp(playableAudioDuration, {
                  omitZeroHours: true,
                  roundMilliseconds: true,
                })}
              </UploaderStatusLabel>
            </UploaderLabelsContainer>
          </MediaInfoContainer>
          <UploaderStatusContainer>
            {isEpisodePublished ? (
              <Button
                color="white"
                className={uploaderCancelButtonStyle}
                onClick={onClickReplace}
                type="button"
              >
                Replace
              </Button>
            ) : (
              <Button
                color="white"
                className={uploaderCancelButtonStyle}
                onClick={onClickDelete}
                type="button"
              >
                Delete
              </Button>
            )}
            {!isVideo && (
              <Button
                color="white"
                className={uploaderEditEpisodeButtonStyle}
                kind="link"
                href={`/dashboard/episode/${webEpisodeId}/edit`}
              >
                Edit your audio
              </Button>
            )}
          </UploaderStatusContainer>
        </UploaderContainer>
        <MultiFileProgressBar
          episodeAudios={episodeAudios}
          onClick={onProgressBarClick}
          onKeyDown={onProgressBarKeyDown}
          activeAudioIndex={activeAudioIndex}
          localPlaybackPosition={localPlaybackPosition}
          playbackPosition={playbackPosition}
          playableAudioDuration={playableAudioDuration}
          getGlobalPlaybackPosition={getGlobalPlaybackPosition}
        />
      </MediaPreviewPlayerWrapper>
      {isVideo && (
        <VideoDisclaimerWrapper>
          Anchor does not currently support video previews, but your video will
          be available on Spotify.
        </VideoDisclaimerWrapper>
      )}
    </MediaPreviewContentWrapper>
  );
};
